import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Pagination } from 'antd';
import { Popup, Selector, SearchBar, Button } from 'antd-mobile';
import { ApplicationListItem, ApplicationStatus } from '@/types';
import { DeleteConfirmDialog } from '@/components/TravelApplication/DeleteConfirmDialog';
import { apiClient } from '@/services/api';

/**
 * 申请列表页面 - Claymorphism 风格
 * 展示所有差旅申请，支持筛选和查看详情
 */
const List: React.FC = () => {
  const [applications, setApplications] = useState<ApplicationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | ''>('');
  const [companionNameFilter, setCompanionNameFilter] = useState('');
  // debounced 版本，延迟 400ms 后才触发搜索
  const [debouncedCompanion, setDebouncedCompanion] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 删除对话框状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // 点击「搜索」时跳过下一次 useEffect 触发的 loadData，避免重复请求
  const skipNextLoadRef = useRef(false);

  // debounce：输入停止 400ms 后更新 debounced 值
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCompanion(companionNameFilter), 400);
    return () => clearTimeout(timer);
  }, [companionNameFilter]);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const [selectedDeleteNo, setSelectedDeleteNo] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 加载数据；可选 overrides 用于「搜索」按钮立即带当前筛选条件请求，避免依赖未变化时不触发
  const loadData = useCallback(async (overrides?: { page?: number; companionName?: string; status?: ApplicationStatus | '' }) => {
    const page = overrides?.page ?? currentPage;
    const companion = overrides?.companionName ?? debouncedCompanion;
    const status = overrides?.status !== undefined ? overrides.status : statusFilter;
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.searchApplications({
        page,
        pageSize,
        statuses: status ? [status] : undefined,
        companionName: companion || undefined,
        sortBy: 'updatedTime',
        sortOrder: 'DESC',
      });

      setApplications(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      console.error('加载数据失败:', err);
      setError(err instanceof Error ? err.message : '加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, debouncedCompanion]);

  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    loadData();
  }, [loadData]);

  // 状态选项
  const statusOptions: { value: ApplicationStatus; label: string; color: string }[] = [
    { value: 'DRAFT', label: '草稿', color: 'bg-slate-100 text-slate-700' },
    { value: 'PENDING_SUBMIT', label: '待提交', color: 'bg-blue-100 text-blue-800' },
    { value: 'APPROVING', label: '审批中', color: 'bg-amber-100 text-amber-800' },
    { value: 'APPROVED', label: '已批准', color: 'bg-green-100 text-green-800' },
    { value: 'REJECTED', label: '已拒绝', color: 'bg-red-100 text-red-800' },
    { value: 'WITHDRAWN', label: '已撤回', color: 'bg-gray-100 text-gray-700' },
    { value: 'TERMINATED', label: '已终止', color: 'bg-slate-100 text-slate-500' },
  ];

  // antd-mobile Selector 选项格式
  const selectorOptions = statusOptions.map(opt => ({
    label: opt.label,
    value: opt.value,
  }));

  const getStatusInfo = (status: ApplicationStatus) => {
    return statusOptions.find(opt => opt.value === status) || { value: status, label: status, color: 'bg-slate-100 text-slate-700' };
  };

  /**
   * 将后端日期字段转为 Date 对象
   * 兼容数组格式 [2026, 3, 5, 9, 0]（Jackson 默认序列化）和字符串格式
   */
  const parseDateField = (date: unknown): Date | null => {
    if (!date) return null;
    if (Array.isArray(date) && date.length >= 3) {
      const [y, m, d, h = 0, min = 0] = date as number[];
      return new Date(y, m - 1, d, h, min);
    }
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const formatDate = (dateField: unknown) => {
    const date = parseDateField(dateField);
    if (!date) return '—';
    return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
  };

  const formatUpdatedTime = (dateField: unknown) => {
    const date = parseDateField(dateField);
    if (!date) return null;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    if (diffMin < 1) return '刚刚';
    if (diffMin < 60) return `${diffMin} 分钟前`;
    if (diffHour < 24) return `今天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `昨天 ${date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) +
      ' ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatAmount = (amount: number) => {
    return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setDebouncedCompanion(companionNameFilter);
    skipNextLoadRef.current = true; // 避免 useEffect 再触发一次相同请求
    loadData({ page: 1, companionName: companionNameFilter });
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setCompanionNameFilter('');
    setDebouncedCompanion('');
    setCurrentPage(1);
    setFilterOpen(false);
    skipNextLoadRef.current = true;
    loadData({ page: 1, companionName: '', status: '' });
  };

  // 打开删除确认对话框
  const openDeleteDialog = (app: ApplicationListItem) => {
    setSelectedDeleteId(app.id);
    setSelectedDeleteNo(app.applicationNo || `#${app.id}`);
    setDeleteDialogOpen(true);
  };

  // 关闭删除确认对话框
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedDeleteId(null);
    setSelectedDeleteNo(null);
  };

  // 执行删除
  const handleDeleteConfirm = async () => {
    if (!selectedDeleteId) return;

    setIsDeleting(true);
    try {
      await apiClient.deleteTravelApplication(String(selectedDeleteId));

      // 删除成功，关闭对话框并刷新列表
      closeDeleteDialog();

      // 刷新列表
      await loadData();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* 页面头部 */}
      <div className="flex flex-row justify-between items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-0.5 sm:mb-2">差旅申请列表</h1>
          <p className="text-xs sm:text-base text-slate-600 hidden sm:block leading-[9.6px]">管理和查看所有差旅申请</p>
        </div>
        <Link
          to="/travel/new"
          className="bg-blue-600 hover:bg-blue-700 px-3 py-2 sm:px-4 sm:py-2 text-white text-sm font-medium flex items-center gap-1.5 cursor-pointer rounded-lg shadow-clay transition-colors flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          新建申请
        </Link>
      </div>

      {/* 筛选器 */}
      <div className="clay-card px-4 py-3">
        {/* 移动端：筛选按钮触发底部弹出 */}
        <div className="flex items-center justify-between lg:hidden">
          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-2 text-sm font-medium text-slate-600 cursor-pointer active:opacity-70"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            筛选
            {(statusFilter || companionNameFilter) && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                {[statusFilter, companionNameFilter].filter(Boolean).length}
              </span>
            )}
          </button>
          {(statusFilter || companionNameFilter) && (
            <button onClick={handleClearFilters} className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer active:opacity-70">
              清除筛选
            </button>
          )}
        </div>

        {/* 移动端底部弹出筛选面板 */}
        <Popup
          visible={filterOpen}
          onMaskClick={() => setFilterOpen(false)}
          onClose={() => setFilterOpen(false)}
          bodyStyle={{ borderTopLeftRadius: '16px', borderTopRightRadius: '16px', minHeight: '40vh' }}
        >
          <div className="px-4 py-6 space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">筛选条件</h3>
              <button onClick={() => setFilterOpen(false)} className="text-slate-400 hover:text-slate-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-3">申请状态</div>
              <Selector
                options={selectorOptions}
                value={statusFilter ? [statusFilter] : []}
                onChange={v => { setStatusFilter(v[0] as ApplicationStatus || ''); setCurrentPage(1); }}
                columns={2}
                multiple={false}
              />
            </div>

            <div>
              <div className="text-sm font-medium text-slate-700 mb-3">同行人姓名</div>
              <SearchBar
                placeholder="搜索姓名"
                value={companionNameFilter}
                onChange={setCompanionNameFilter}
                onSearch={handleSearch}
                onClear={() => setCompanionNameFilter('')}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                block
                size="large"
                fill="outline"
                onClick={handleClearFilters}
              >
                清除
              </Button>
              <Button
                block
                size="large"
                color="primary"
                onClick={() => { handleSearch(); setFilterOpen(false); }}
              >
                确定
              </Button>
            </div>
          </div>
        </Popup>

        {/* 桌面端：横排筛选（保持原样） */}
        <div className="hidden lg:flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">状态</span>
            <div className="relative w-[150px]">
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value as ApplicationStatus | ''); setCurrentPage(1); }}
                className="w-full pl-3 pr-8 py-2 rounded-lg text-sm border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer appearance-none bg-no-repeat"
                style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2364748b\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'/%3E%3C/svg%3E")', backgroundPosition: 'right 12px center', backgroundSize: '16px 16px' }}
              >
                <option value="">全部状态</option>
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600 whitespace-nowrap">同行人</span>
            <input
              type="text"
              value={companionNameFilter}
              onChange={e => setCompanionNameFilter(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="搜索姓名"
              className="px-3 py-2 rounded-lg text-sm border border-slate-200 bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300 w-[150px]"
            />
          </div>
          <div className="flex gap-2 ml-auto">
            <button
              onClick={handleSearch}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 cursor-pointer transition-colors"
            >
              搜索
            </button>
            {(statusFilter || companionNameFilter) && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-blue-700 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                清除
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 列表内容 */}
      {loading ? (
        <div className="clay-card p-12 text-center">
          <div className="inline-block w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-slate-600">加载中...</p>
        </div>
      ) : error ? (
        <div className="clay-card p-12 text-center">
          <p className="text-red-600 mb-4 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </p>
          <button
            onClick={() => loadData()}
            className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-6 py-2 font-medium cursor-pointer rounded-lg transition-colors"
          >
            重试
          </button>
        </div>
      ) : applications.length === 0 ? (
        <div className="clay-card p-12 text-center">
          <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-slate-600">暂无申请记录</p>
          <Link
            to="/travel/new"
            className="inline-block mt-4 bg-blue-50 hover:bg-blue-100 text-blue-700 px-6 py-2 font-medium cursor-pointer rounded-lg transition-colors"
          >
            创建第一个申请
          </Link>
        </div>
      ) : (
        <div className="clay-card overflow-hidden">
          {/* 桌面端表格 */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">申请单号</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">申请人</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">类别</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">地点/路线</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">日期</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">预估费用</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">状态</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {applications.map((app) => {
                  const statusInfo = getStatusInfo(app.status);
                  const updatedText = formatUpdatedTime(app.updatedTime);
                  return (
                    <tr key={app.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-slate-800">{app.applicationNo ? app.applicationNo : `#${app.id}`}</p>
                        {updatedText && (
                          <p className="text-xs text-slate-400 mt-0.5">{updatedText}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {app.applicantName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {app.category === 'LOCAL'
                          ? <span className="inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium bg-teal-50 text-teal-700 border border-teal-100">同城</span>
                          : <span className="inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">跨城</span>
                        }
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700">
                        {(() => {
                          const isLocal = app.category === 'LOCAL'
                          if (isLocal) return app.location || '未填写'
                          const dest = app.destinationCities && app.destinationCities.length > 0
                            ? app.destinationCities.join('、')
                            : (app.destinationCity || '')
                          return `${app.departureCity || ''} → ${dest}`
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {formatDate(app.startDate)} - {formatDate(app.endDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-800">
                        {app.category === 'LOCAL' 
                          ? <span className="text-slate-400">-</span>
                          : formatAmount(app.estimatedExpense)
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-1 rounded-lg text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-1">
                          <Link
                            to={`/travel/${app.id}/view`}
                            className="inline-block w-10 text-center text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                          >
                            查看
                          </Link>
                          {(app.status === 'DRAFT' || app.status === 'WITHDRAWN' || app.status === 'REJECTED') ? (
                            <>
                              <Link
                                to={`/travel/${app.id}/edit`}
                                className="inline-block w-10 text-center text-blue-600 hover:text-blue-800 hover:underline font-medium cursor-pointer"
                              >
                                编辑
                              </Link>
                              {app.status === 'DRAFT' ? (
                                <button
                                  onClick={() => openDeleteDialog(app)}
                                  className="inline-block w-10 text-center text-red-600 hover:text-red-800 font-medium cursor-pointer"
                                >
                                  删除
                                </button>
                              ) : (
                                <span className="inline-block w-10" />
                              )}
                            </>
                          ) : (
                            <>
                              <span className="inline-block w-10" />
                              <span className="inline-block w-10" />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 移动端卡片列表 */}
          <div className="lg:hidden divide-y divide-slate-100">
            {applications.map((app) => {
              const statusInfo = getStatusInfo(app.status);
              const updatedText = formatUpdatedTime(app.updatedTime);
              return (
                <div key={app.id} className="hover:bg-slate-50/50">
                  {/* 卡片主体 */}
                  <div className="px-4 pt-4 pb-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">
                          {app.applicationNo ? app.applicationNo : `#${app.id}`}
                        </p>
                        {updatedText && (
                          <p className="text-xs text-slate-400 mt-0.5">{updatedText}</p>
                        )}
                        <p className="text-sm text-slate-600 flex items-center gap-1.5 mt-1">
                          {app.applicantName}
                          {app.category === 'LOCAL'
                            ? <span className="inline-flex px-1.5 py-0 rounded text-[10px] font-medium bg-teal-50 text-teal-700 border border-teal-100">同城</span>
                            : <span className="inline-flex px-1.5 py-0 rounded text-[10px] font-medium bg-orange-50 text-orange-700 border border-orange-100">跨城</span>
                          }
                        </p>
                      </div>
                      <span className={`ml-3 flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">
                      {app.category === 'LOCAL'
                        ? (app.location || '未填写')
                        : (() => {
                            const dest = app.destinationCities && app.destinationCities.length > 0
                              ? app.destinationCities.join('、')
                              : (app.destinationCity || '')
                            return `${app.departureCity || ''} → ${dest}`
                          })()
                      }
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatDate(app.startDate)} - {formatDate(app.endDate)}
                    </p>
                  </div>
                  {/* 底部操作栏，用分割线隔开 */}
                  <div className="border-t border-slate-100 px-4 py-2.5 flex justify-between items-center">
                    <p className="text-sm font-medium text-slate-700">
                      {app.category === 'LOCAL'
                        ? <span className="text-slate-400 text-xs">同城出差</span>
                        : formatAmount(app.estimatedExpense)
                      }
                    </p>
                    <div className="flex items-center divide-x divide-slate-200">
                      <Link
                        to={`/travel/${app.id}/view`}
                        className="px-3 text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                      >
                        查看
                      </Link>
                      {(app.status === 'DRAFT' || app.status === 'WITHDRAWN' || app.status === 'REJECTED') && (
                        <Link
                          to={`/travel/${app.id}/edit`}
                          className="px-3 text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                        >
                          编辑
                        </Link>
                      )}
                      {app.status === 'DRAFT' && (
                        <button
                          onClick={() => openDeleteDialog(app)}
                          className="pl-3 text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer"
                        >
                          删除
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 分页 */}
          {total > 0 && (
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                pageSizeOptions={[10, 20, 50]}
                showTotal={(t) => `共 ${t} 条`}
                onChange={(page, size) => {
                  setCurrentPage(page);
                  if (size !== pageSize) {
                    setPageSize(size);
                    setCurrentPage(1);
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* 删除确认对话框 */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        applicationNo={selectedDeleteNo || ''}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={closeDeleteDialog}
      />
    </div>
  );
};

export default List;
